import type { RankCommand, RankResult, TaskScopedList } from "@mind/contracts/browser";
import { compareRanks } from "./board.js";

type BoardTask = TaskScopedList["items"][number];

export function applyOptimisticMove(data: TaskScopedList, command: RankCommand): TaskScopedList {
  const moving = data.items.find((task) => task.id === command.taskId);
  if (moving === undefined) return data;

  const withoutMoving = data.items.filter((task) => task.id !== command.taskId);
  const destination = withoutMoving
    .filter((task) => task.projectId === moving.projectId && task.status === command.toStatus)
    .slice()
    .sort((left, right) => compareRanks(left.rank, right.rank) || left.id.localeCompare(right.id));
  const afterIndex = command.afterTaskId === null ? -1 : destination.findIndex((task) => task.id === command.afterTaskId);
  const beforeIndex = command.beforeTaskId === null ? -1 : destination.findIndex((task) => task.id === command.beforeTaskId);
  const insertionIndex = afterIndex >= 0
    ? afterIndex
    : beforeIndex >= 0
      ? beforeIndex + 1
      : destination.length;
  const nextDestination = [...destination];
  nextDestination.splice(insertionIndex, 0, { ...moving, status: command.toStatus });

  const source = command.fromStatus === command.toStatus
    ? []
    : withoutMoving
      .filter((task) => task.projectId === moving.projectId && task.status === command.fromStatus)
      .slice()
      .sort((left, right) => compareRanks(left.rank, right.rank) || left.id.localeCompare(right.id));
  const updates = new Map<string, BoardTask>();
  for (const [index, task] of source.entries()) {
    updates.set(task.id, { ...task, rank: String((index + 1) * 1024) });
  }
  for (const [index, task] of nextDestination.entries()) {
    updates.set(task.id, { ...task, rank: String((index + 1) * 1024) });
  }

  return {
    ...data,
    items: data.items.map((task) => updates.get(task.id) ?? task),
  };
}

export function applyAuthoritativeRank(data: TaskScopedList, result: RankResult): TaskScopedList {
  const moving = data.items.find((task) => task.id === result.taskId);
  if (moving === undefined) return data;
  const ordered = new Map(result.columnOrder?.map((item) => [item.taskId, item]) ?? []);
  return {
    ...data,
    items: data.items.map((task) => {
      const authoritative = ordered.get(task.id);
      if (task.id === result.taskId) {
        return {
          ...task,
          status: result.status,
          rank: authoritative?.rank ?? result.rank,
          revision: authoritative?.revision ?? result.taskRevision,
        };
      }
      return authoritative === undefined
        ? task
        : { ...task, rank: authoritative.rank, revision: authoritative.revision };
    }),
    projectBoardRevisions: data.projectBoardRevisions.map((revision) => revision.projectId === moving.projectId
      ? { ...revision, boardRevision: result.boardRevision }
      : revision),
  };
}

export function rollbackOptimisticMove(previous: TaskScopedList): TaskScopedList {
  return previous;
}
